import React from "react";

import {
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Grid,
  Typography,
  Chip,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import StarIcon from "@mui/icons-material/Star";
import { useMutation } from "@tanstack/react-query";
import { Bounce, toast } from "react-toastify";

import { pricingPlanService } from "../../services";
import { UpgradePricingPlanPayload } from "../../types";
import { useUserState } from "../../../../../store";

const ModernCard = styled(Card)(() => ({
  position: "relative",
  borderRadius: 20,
  background: "#ffffff",
  border: "1px solid rgba(0, 0, 0, 0.08)",
  boxShadow: "0 4px 20px rgba(0, 0, 0, 0.08)",
  transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
  overflow: "visible",
  height: "100%",
  display: "flex",
  flexDirection: "column",
  "&:hover": {
    transform: "translateY(-8px)",
    boxShadow: "0 12px 40px rgba(0, 0, 0, 0.15)",
  },
  "&.popular": {
    border: "2px solid #6366f1",
    boxShadow: "0 8px 32px rgba(99, 102, 241, 0.15)",
    "&:hover": {
      boxShadow: "0 16px 48px rgba(99, 102, 241, 0.25)",
    },
  },
}));

const PopularBadge = styled(Chip)(() => ({
  position: "absolute",
  top: -16,
  left: "50%",
  transform: "translateX(-50%)",
  background: "linear-gradient(135deg, #6366f1, #8b5cf6)",
  color: "#ffffff",
  fontWeight: 700,
  fontSize: "0.75rem",
  height: 32,
  borderRadius: 16,
  "& .MuiChip-label": {
    padding: "0 16px",
  },
  "& .MuiChip-icon": {
    color: "#fbbf24",
    marginRight: "4px",
  },
}));

const PlanHeader = styled(Box)(() => ({
  textAlign: "center",
  marginBottom: 24,
  "& .plan-name": {
    fontSize: "1.5rem",
    fontWeight: 700,
    color: "#1f2937",
    marginBottom: "8px",
  },
  "& .plan-description": {
    fontSize: "0.85rem",
    color: "#6b7280",
    lineHeight: 1.5,
  },
  "& .credits": {
    fontSize: "1.1rem",
    color: "#1f2937",
    fontWeight: 600,
    marginTop: "8px",
  },
}));

const PriceSection = styled(Box)(() => ({
  display: "flex",
  justifyContent: "center",
  "& .price": {
    fontSize: "2.0rem",
    fontWeight: 800,
    lineHeight: 1,
    color: "#1f2937",
    marginBottom: "20px",
  },
  "& .period": {
    fontSize: "0.9rem",
    color: "#6b7280",
    fontWeight: 500,
  },
}));

const FeatureList = styled(Box)(() => ({
  marginBottom: 32,
  flex: 1,
  "& .feature-item": {
    display: "flex",
    alignItems: "center",
    marginBottom: "16px",
    "& .MuiSvgIcon-root": {
      color: "#10b981",
      marginRight: "12px",
      fontSize: "1.1rem",
    },
    "& .MuiTypography-root": {
      color: "#374151",
      fontSize: "0.85rem",
      lineHeight: 1.5,
      fontWeight: 500,
    },
  },
}));

const ModernButton = styled(Button)(({ theme }) => ({
  borderRadius: 12,
  textTransform: "none",
  fontWeight: 700,
  fontSize: "0.9rem",
  padding: theme.spacing(1.5, 3),
  minHeight: 48,
  transition: "all 0.2s cubic-bezier(0.4, 0, 0.2, 1)",
  "&.MuiButton-contained": {
    background: "linear-gradient(135deg, #1976d2, #1565c0)",
    boxShadow: "0 4px 16px rgba(25, 118, 210, 0.3)",
    "&:hover": {
      background: "linear-gradient(135deg, #1565c0, #0d47a1)",
      boxShadow: "0 6px 24px rgba(25, 118, 210, 0.4)",
      transform: "translateY(-2px)",
    },
  },
  "&.MuiButton-outlined": {
    borderColor: "#6366f1",
    color: "#6366f1",
    backgroundColor: "rgba(99, 102, 241, 0.05)",
    borderWidth: "2px",
    "&:hover": {
      backgroundColor: "rgba(99, 102, 241, 0.1)",
      borderColor: "#4f46e5",
    },
  },
  "&:disabled": {
    background: "rgba(156, 163, 175, 0.1)",
    color: "rgba(156, 163, 175, 0.8)",
    border: "1px solid rgba(156, 163, 175, 0.2)",
  },
}));

const GradientButton = styled(ModernButton)(() => ({
  "&.MuiButton-contained": {
    background: "linear-gradient(135deg, #1976d2, #1565c0)",
    boxShadow: "0 4px 16px rgba(25, 118, 210, 0.3)",
    "&:hover": {
      background: "linear-gradient(135deg, #1565c0, #0d47a1)",
      boxShadow: "0 6px 24px rgba(25, 118, 210, 0.4)",
      transform: "translateY(-2px)",
    },
  },
}));

interface Props {
  pricingPlansData: Record<string, any>[];
}

const formatFeaturesFromAPI = (features: any[]): string[] => {
  if (!Array.isArray(features)) {
    return [];
  }

  return features.map((featureQuota) => {
    const { quota, feature } = featureQuota;
    const unitName = feature.unit_name || "unit";
    const unitNamePlural = quota === 1 ? unitName : `${unitName}s`;

    if (feature.name === "available_seats") {
      return `${quota} ${unitNamePlural}`;
    }
    if (feature.name === "number_of_custom_prompts_to_short_video_per_month") {
      return `${quota} custom video prompts per month`;
    }
    if (
      feature.name === "number_of_custom_prompts_to_textual_content_per_month"
    ) {
      return `${quota} custom content prompts per month`;
    }
    return `${quota} ${feature.description || unitNamePlural}`;
  });
};

export default function PricingPlanCard({ pricingPlansData }: Props) {
  const { mutateAsync, isSuccess, error, data, isPending } = useMutation({
    mutationKey: ["upgradePricingPlan"],
    mutationFn: (values: UpgradePricingPlanPayload) =>
      pricingPlanService.upgradePricingPlan(values),
  });
  const { user } = useUserState();

  const handlePricingPlanUpgrade = async (
    planId: string,
    recurringInterval: string,
  ) => {
    const payload = {
      pricing_plan_id: planId,
      pricing_cycle: recurringInterval,
    };
    await toast.promise(mutateAsync(payload), {
      error: {
        toastId: `Error`,
        render() {
          return `Error: ${JSON.stringify(error, null, 2)}`;
        },
        type: "error",
        theme: "colored",
        autoClose: false,
        transition: Bounce,
        delay: 500,
      },
    });
  };

  if (isSuccess) {
    window.location.replace(data.url);
  }

  if (isPending) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
        <CircularProgress size={40} sx={{ color: "#3b82f6" }} />
      </Box>
    );
  }

  return (
    <Grid container spacing={3} sx={{ mt: 2 }}>
      {pricingPlansData?.map((pricingPlanData) => {
        const isCurrentPlan = !!(
          user?.organization?.subscription?.pricing_plan?.id &&
          user?.organization?.subscription?.pricing_plan?.id ===
            pricingPlanData.id
        );

        const isDisabled =
          pricingPlanData.is_allowed_to_purchase === false ||
          user?.organization?.subscription?.pricing_cycle === "yearly";

        const isPopular = !isCurrentPlan && pricingPlanData.is_popular;

        const features =
          Array.isArray(pricingPlanData.features) &&
          pricingPlanData.features.length > 0
            ? formatFeaturesFromAPI(pricingPlanData.features)
            : [];

        return (
          <Grid item xs={12} sm={6} md={3} key={pricingPlanData.id}>
            <ModernCard
              className={isPopular ? "popular" : ""}
              sx={{
                opacity: isDisabled ? 0.6 : 1,
              }}
            >
              {isPopular && (
                <PopularBadge
                  label="Popular"
                  icon={<StarIcon />}
                  variant="filled"
                />
              )}
              <CardContent
                sx={{
                  p: 4,
                  flex: 1,
                  display: "flex",
                  flexDirection: "column",
                }}
              >
                {/* Plan Header */}
                <PlanHeader>
                  <Typography className="plan-name">
                    {pricingPlanData.name}
                  </Typography>
                  <Typography className="plan-description">
                    {pricingPlanData.description}
                  </Typography>
                  {pricingPlanData.credits_per_month && (
                    <Typography className="credits">
                      {pricingPlanData.credits_per_month.toLocaleString()}{" "}
                      credits/month
                    </Typography>
                  )}
                </PlanHeader>

                {/* Price Section */}
                <PriceSection>
                  <Box sx={{ display: "flex", alignItems: "baseline", gap: 1 }}>
                    <Typography className="price">
                      {pricingPlanData.price_in_cents || "Free"}
                    </Typography>
                    {pricingPlanData.price_in_cents && (
                      <Typography className="period">
                        / {pricingPlanData.recurring_interval}
                      </Typography>
                    )}
                  </Box>
                </PriceSection>

                {/* Action Button */}
                {isPopular ? (
                  <GradientButton
                    variant={isCurrentPlan ? "outlined" : "contained"}
                    fullWidth
                    onClick={() =>
                      handlePricingPlanUpgrade(
                        pricingPlanData.id,
                        pricingPlanData.recurring_interval,
                      )
                    }
                    disabled={isDisabled || isCurrentPlan}
                    sx={{ mb: 3 }}
                  >
                    {isCurrentPlan ? "✓ Current Plan" : "Upgrade"}
                  </GradientButton>
                ) : (
                  <ModernButton
                    variant={isCurrentPlan ? "outlined" : "contained"}
                    fullWidth
                    onClick={() =>
                      handlePricingPlanUpgrade(
                        pricingPlanData.id,
                        pricingPlanData.recurring_interval,
                      )
                    }
                    disabled={isDisabled || isCurrentPlan}
                    sx={{ mb: 3 }}
                  >
                    {isCurrentPlan ? "✓ Current Plan" : "Upgrade"}
                  </ModernButton>
                )}

                {/* Features List */}
                {features.length > 0 && (
                  <FeatureList>
                    {features.map((feature: string) => (
                      <Box key={feature} className="feature-item">
                        <CheckCircleOutlineIcon />
                        <Typography>{feature}</Typography>
                      </Box>
                    ))}
                  </FeatureList>
                )}
              </CardContent>
            </ModernCard>
          </Grid>
        );
      })}
    </Grid>
  );
}
